import { useQuery } from '@tanstack/react-query';
import { CircleAlert, HardDrive } from 'lucide-react';
import type { ReactNode } from 'react';

import { EmptyState } from '../../components/EmptyState';
import { StatusBadge, type Tone } from '../../components/StatusBadge';
import { useTimeFormat } from '../../lib/useTimeFormat';
import { useAuth } from '../auth/AuthProvider';
import { recordingErrorLabels } from '../recording/api';
import { formatBytes, tenantName } from './media';
import {
  daysUntilFull,
  diskTone,
  getRecordingHealth,
  recordingHealthKey,
  type RecordingHealth,
} from './recordingHealth';

/**
 * Recording state of every tenant's cameras (the super administrator's view,
 * 0.67.0) and use of the recording volume.
 */
export function RecordingHealthPanel() {
  const { request } = useAuth();
  const q = useQuery({
    queryKey: recordingHealthKey,
    queryFn: () => getRecordingHealth(request),
    refetchInterval: 30_000,
  });

  const badge = (h: RecordingHealth): { tone: Tone; label: string } => {
    if (!h.available) return { tone: 'neutral', label: 'Não configurada' };
    if (h.cameras.failing > 0)
      return {
        tone: 'crit',
        label:
          h.cameras.failing === 1
            ? '1 câmera com erro'
            : `${String(h.cameras.failing)} câmeras com erro`,
      };
    if (h.cameras.recording + h.cameras.starting === 0)
      return {
        tone: 'neutral',
        label: h.cameras.off_schedule
          ? 'Fora do horário da agenda'
          : h.cameras.waiting_event
            ? 'Aguardando evento'
            : 'Nenhuma gravação',
      };
    return { tone: 'ok', label: 'Gravando' };
  };

  return (
    <section
      aria-labelledby="rec-health-heading"
      className="overflow-hidden rounded-lg border border-line bg-surface"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <div className="flex items-center gap-3">
          <h3 id="rec-health-heading" className="text-[13px] font-medium">
            Gravação
          </h3>
          {q.data && <StatusBadge tone={badge(q.data).tone}>{badge(q.data).label}</StatusBadge>}
        </div>
        <p className="text-xs text-fg-subtle">atualiza a cada 30 s</p>
      </div>
      {q.isPending && <div aria-hidden="true" className="h-24 animate-pulse bg-surface-hover/40" />}
      {q.isError && (
        <EmptyState
          tone="crit"
          icon={CircleAlert}
          title="Não foi possível carregar o estado da gravação"
          description="A API não respondeu a esta consulta."
        />
      )}
      {q.data && <Body h={q.data} />}
    </section>
  );
}

function Body({ h }: { h: RecordingHealth }) {
  const fmt = useTimeFormat();
  if (!h.available) {
    return (
      <EmptyState
        icon={HardDrive}
        title="Armazenamento de gravação não configurado"
        description="Defina RECORDING_DIR na implantação para que as câmeras possam gravar."
      />
    );
  }
  const c = h.cameras;
  const perDay = h.storage.last_hour_bytes * 24;
  const disk = h.disk;
  const tone = disk ? diskTone(disk.total_bytes, disk.free_bytes) : 'neutral';
  const usedPct = disk ? Math.round((1 - disk.free_bytes / disk.total_bytes) * 100) : 0;
  const inBucket = h.storage.provider === 's3';
  // In a bucket, recordings do not fill the server's disk: no projection.
  const days = disk && !inBucket ? daysUntilFull(disk.free_bytes, h.storage.last_hour_bytes) : null;

  return (
    <>
      <div className="grid grid-cols-2 gap-px border-b border-line bg-line lg:grid-cols-4">
        <Tile label="Câmeras gravando" value={String(c.recording)}>
          {[
            c.starting > 0 ? `${String(c.starting)} iniciando` : '',
            c.off_schedule ? `${String(c.off_schedule)} fora do horário da agenda` : '',
            c.waiting_event ? `${String(c.waiting_event)} aguardando evento` : '',
          ]
            .filter(Boolean)
            .join(' · ') || 'confirmadas pelo supervisor'}
        </Tile>
        <Tile label="Com erro" value={String(c.failing)} tone={c.failing > 0 ? 'crit' : undefined}>
          {c.failing > 0 ? 'veja a lista abaixo' : 'nenhum problema'}
        </Tile>
        <Tile label="Sem gravação" value={String(c.disabled)}>
          gravação desligada ou pausada (equipamento desativado, conta, cliente ou empresa
          suspensos)
        </Tile>
        <Tile label="Usado pelas empresas" value={formatBytes(h.storage.bytes)}>
          {h.storage.oldest_recording
            ? `desde ${fmt.absolute(h.storage.oldest_recording)} · ${formatBytes(perDay)}/dia no ritmo atual`
            : 'nenhuma gravação armazenada'}
        </Tile>
      </div>

      {inBucket && (
        <p className="border-b border-line px-4 py-2.5 text-xs text-fg-muted">
          As gravações vão para um <span className="text-fg">bucket S3</span> na nuvem. O disco do
          servidor guarda só os trechos em envio e as exportações.
        </p>
      )}
      {disk && (
        <div className="border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="text-fg-muted">
              {inBucket
                ? 'Disco do servidor (envio e exportações):'
                : 'Disco de gravação do servidor (sem bucket configurado):'}{' '}
              <span className="text-fg tabular-nums">
                {formatBytes(disk.total_bytes - disk.free_bytes)} de {formatBytes(disk.total_bytes)}
              </span>{' '}
              ({usedPct}% usado · {formatBytes(disk.free_bytes)} livres)
            </span>
            <StatusBadge tone={tone}>
              {tone === 'crit' ? 'Quase cheio' : tone === 'warn' ? 'Atenção' : 'Normal'}
            </StatusBadge>
          </div>
          <div
            role="meter"
            aria-label="Uso do disco de gravação"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={usedPct}
            aria-valuetext={`${String(usedPct)}% usado`}
            className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-hover"
          >
            <div
              className={`h-full rounded-full ${tone === 'crit' ? 'bg-crit' : tone === 'warn' ? 'bg-warn' : 'bg-accent'}`}
              style={{ width: `${String(usedPct)}%` }}
            />
          </div>
          <p className="mt-1.5 text-2xs text-fg-subtle">
            {inBucket
              ? 'Se o bucket ficar fora do ar, os trechos esperam aqui e sobem quando ele voltar.'
              : days === null
                ? 'Nada foi gravado na última hora.'
                : `No ritmo da última hora de todas as empresas, o espaço livre dura ${
                    days >= 365
                      ? 'mais de um ano'
                      : `cerca de ${String(Math.max(Math.floor(days), 0))} dias`
                  } (sem contar o que a retenção libera).`}
          </p>
        </div>
      )}

      {h.failures.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <caption className="sr-only">Câmeras com erro de gravação</caption>
            <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
              <tr className="border-b border-line">
                <th scope="col" className="px-4 py-2 font-medium">
                  Empresa
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Câmera
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Motivo
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Desde
                </th>
              </tr>
            </thead>
            <tbody>
              {h.failures.map((f) => (
                <tr key={f.device.id} className="border-b border-line last:border-0 align-top">
                  {/* No link: the platform has no device screen. */}
                  <td className="px-4 py-2.5 text-fg">{tenantName(f.tenant)}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-fg">{f.device.name}</div>
                    <div className="text-2xs text-fg-subtle">{f.device.site}</div>
                  </td>
                  <td className="px-4 py-2.5 text-fg-muted">{recordingErrorLabels[f.code]}</td>
                  <td className="px-4 py-2.5 text-right text-xs whitespace-nowrap text-fg-muted">
                    {f.since ? fmt.relative(f.since) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Tile({
  label,
  value,
  tone,
  children,
}: {
  label: string;
  value: string;
  tone?: Tone | undefined;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 bg-surface px-4 py-3">
      <span className="text-2xs tracking-wide text-fg-subtle uppercase">{label}</span>
      <div
        className={`mt-1 text-xl font-semibold tabular-nums ${tone === 'crit' ? 'text-crit' : 'text-fg'}`}
      >
        {value}
      </div>
      <div className="mt-1 text-xs text-fg-muted">{children}</div>
    </div>
  );
}
